import { useCallback, useMemo } from 'react';
import { DocumentNode, useApolloClient } from '@apollo/client';
import { liveOrderListContext } from 'apollo/order-live-updates';
import { removeOrdersFromList } from 'apollo/order-list-cache';
import { Order } from 'models/order';

/**
 * One paged order list that stays live (DD-3231). Pass `context` to the list query: it
 * registers the list with the ORDER_CHANGED handler, which then re-reads a changed row with
 * `rowQuery` and the whole loaded window when orders come and go. Read the rows with
 * `ordersFrom(data)` — straight from the cache, never a copy.
 *
 * A mutation that returns the Order needs no callback: Apollo updates the row itself. The
 * one list edit the cache cannot infer is an order leaving the list (archived, sent, moved
 * to another step); that is `removeOrders`.
 */
export const useLiveOrderList = (listField: string, rowQuery: DocumentNode) => {
    const client = useApolloClient();
    const context = useMemo(() => liveOrderListContext(listField, rowQuery), [listField, rowQuery]);

    const ordersFrom = useCallback(
        (data: Record<string, unknown> | undefined): Order[] => (data?.[listField] as Order[] | undefined) ?? [],
        [listField],
    );

    const removeOrders = useCallback(
        (orderIds: Iterable<string | undefined>) =>
            removeOrdersFromList(
                client.cache,
                listField,
                Array.from(orderIds).filter((orderId): orderId is string => !!orderId),
            ),
        [client, listField],
    );

    return { context, ordersFrom, removeOrders };
};
